import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { payApiClient } from '@/api/client';
import { ApiRequestError } from '@/api/errors';
import { fetchAllAdminTenants } from '@/api/fetchAll';
import type { AdminGatewayAccount, TestBenchPreset } from '@/api/models';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { FilterableSelect } from '@/components/form/FilterableSelect';
import { tenantToOption } from '@/components/form/optionMappers';
import { useApiResource } from '@/hooks/useApiResource';

interface TestBenchPresetLinkDialogProps {
  open: boolean;
  onClose: () => void;
  onLinked: (account: Pick<AdminGatewayAccount, 'id'>) => void;
}

/**
 * One-click sandbox preset link: `POST /v1/admin/test-bench/presets/{code}/link`
 * (D4 — only `redsys-sandbox` ships prefilled). Creates the gateway account
 * and writes its credential in one call; this dialog never collects or
 * shows a credential value itself.
 */
export function TestBenchPresetLinkDialog({ open, onClose, onLinked }: TestBenchPresetLinkDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [tenantCode, setTenantCode] = useState('');
  const [presetCode, setPresetCode] = useState('');
  const [alias, setAlias] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // MUI's `Dialog` mounts its children even while `open` is `false` (it only
  // withholds the visual presentation), so both fetchers below guard on
  // `open` themselves — otherwise the parent screen would trigger these
  // calls the moment it renders, dialog closed or not.
  const tenants = useApiResource(
    () => (open ? fetchAllAdminTenants() : Promise.resolve([])),
    `preset-dialog-tenants-${open}`,
  );
  const presets = useApiResource(() => (open ? payApiClient.getTestBenchPresets() : Promise.resolve([])), `preset-dialog-presets-${open}`);

  useEffect(() => {
    if (open) {
      setTenantCode('');
      setPresetCode('');
      setAlias('');
      setAccessDenied(false);
    }
  }, [open]);

  useEffect(() => {
    if (!presetCode && presets.data && presets.data.length > 0) {
      setPresetCode(presets.data[0]?.code ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presets.data]);

  function resetAndClose() {
    setTenantCode('');
    setPresetCode('');
    setAlias('');
    setAccessDenied(false);
    onClose();
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const response = await payApiClient.linkTestBenchPreset(presetCode, {
        tenantCode: tenantCode.trim(),
        alias: alias.trim() || undefined,
      });
      notify(translate('pay.testBench.preset.success'), { type: 'success' });
      resetAndClose();
      onLinked({ id: response.gatewayAccountId });
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  const presetOptions: TestBenchPreset[] = presets.data ?? [];
  const canSubmit = tenantCode.trim().length > 0 && presetCode.trim().length > 0 && !submitting;

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="xs" fullWidth>
      <DialogTitle>{translate('pay.testBench.preset.dialogTitle')}</DialogTitle>
      <DialogContent>
        {accessDenied ? (
          <AccessDenied />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <FilterableSelect
              autoFocus
              label={translate('pay.testBench.preset.fieldTenant')}
              options={(tenants.data ?? []).map(tenantToOption)}
              loading={tenants.loading}
              value={tenantCode}
              onChange={setTenantCode}
            />
            <FilterableSelect
              disableClearable
              label={translate('pay.testBench.preset.fieldPreset')}
              options={presetOptions.map((preset) => ({ value: preset.code, label: preset.name, secondary: preset.code }))}
              loading={presets.loading}
              value={presetCode}
              onChange={(code) => {
                if (code) setPresetCode(code);
              }}
              helperText={presetOptions.length === 0 ? translate('pay.testBench.preset.noPresets') : undefined}
            />
            <TextField
              size="small"
              label={translate('pay.testBench.preset.fieldAlias')}
              helperText={translate('pay.testBench.preset.fieldAliasHelp')}
              value={alias}
              onChange={(event) => setAlias(event.target.value)}
              fullWidth
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose}>{translate('pay.testBench.preset.cancel')}</Button>
        {!accessDenied && (
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
            {translate('pay.testBench.preset.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
